import { alpha, createTheme, lighten, Theme, ThemeOptions } from '@mui/material/styles'

// A pill-shaped button reserved for the encounter recorder's single start/stop control —
// the one action a clinician hits without looking. Every other button stays the standard
// 8px-radius rectangle; this is a named theme variant instead of a per-call-site borderRadius
// override so it can't be reached for by anything else.
declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        heroAction: true
    }
}

type FocusRingColorKey = 'primary' | 'error' | 'warning' | 'info' | 'success'

// Buttons only carry `color="inherit"` or a custom string in a handful of edge cases; falling
// back to primary there keeps the focus ring always painting *some* theme color instead of
// silently no-op'ing. This app's secondary.main is white (a reversed style meant for colored
// surfaces), so a ring painted in it would be invisible against a light background — it falls
// back to primary as well, without touching the button's own fill color.
const resolveFocusRingColorKey = (color: string | undefined): FocusRingColorKey => {
    switch (color) {
        case 'error':
        case 'warning':
        case 'info':
        case 'success':
            return color
        default:
            return 'primary'
    }
}

// The side panels' tone: a shade darker than the canvas, so they frame the central content
export const sidePanelColor = '#F1EBE5'

// The hairline separating the sidebar from the main content next to it, warm to sit on the sand canvas
export const shellHairline = '#EDE4DF'

// Live audio-level bars in the recording visualizer. A high-visibility teal-green that signals to the
// clinician that their voice is being captured; kept deliberately outside the muted brand palette so it
// stays legible at the small bar size.
export const audioLevelColor = '#20A4A9'

// Shared primary-color tint for hover/selected backgrounds and borders, so every call site
// tracks the brand color from one place instead of each re-deriving it from primary.main.
export const primaryTint = (theme: Theme, opacity: number): string => alpha(theme.palette.primary.main, opacity)

// The selected sidebar entry: a light tint of the brand navy, light enough to sit in with the warm
// sand canvas rather than read as a cool grey block
export const sidebarSelectedTint = (theme: Theme): string => primaryTint(theme, 0.14)

// Opaque equivalent of primaryTint, for backgrounds that sit over scrollable content (e.g. a
// sticky toolbar) where a translucent color would let rows show through underneath.
export const primarySolidTint = (theme: Theme, coefficient: number): string =>
    lighten(theme.palette.primary.main, coefficient)

// Height of a large button; controls placed beside one use the same height
export const LARGE_BUTTON_HEIGHT = 56

// Semantic status colors, muted to match the brand's restrained palette instead of MUI's
// stock Material colors — each clears >=4.5:1 contrast for white text on a filled Alert.
const statusPalette = {
    error: '#B02A2A', // brand red, matched from medendo.com's own form-validation banners
    success: '#4C8066', // darkened sage (same hue as Groups/groupColors.ts sage green)
    info: '#0072B2', // calm blue accent, distinct from the brand primary and status colors
} as const

const colorSchemes: Record<'light' | 'dark', ThemeOptions> = {
    light: {
        palette: {
            mode: 'light',
            primary: {
                main: '#3D4470',
                contrastText: '#fff',
            },
            secondary: {
                main: '#fff',
                contrastText: '#3D4470',
            },
            error: {
                main: statusPalette.error,
            },
            success: {
                main: statusPalette.success,
            },
            info: {
                main: statusPalette.info,
            },
            background: {
                // White surfaces (the encounter sheet, list tables) stand out from it by tone alone
                default: '#F7F2EE',
                paper: '#fff',
            },
            text: {
                // Near-black with a hint of the brand navy: softer than pure black for long reading
                primary: '#1C1D26',
                secondary: 'rgba(0, 0, 0, 0.6)',
            },
        },
    },
    dark: {
        palette: {
            mode: 'dark',
            primary: {
                main: '#3D4470',
                contrastText: '#fff',
            },
            secondary: {
                main: '#fff',
                contrastText: '#3D4470',
            },
            error: {
                main: statusPalette.error,
            },
            success: {
                main: statusPalette.success,
            },
            info: {
                main: statusPalette.info,
            },
            background: {
                default: '#121212',
                paper: '#1e1e1e',
            },
            text: {
                primary: '#fff',
                secondary: '#bbb',
            },
        },
    },
}

const typography: ThemeOptions['typography'] = {
    fontFamily: `Poppins, sans-serif`,
    h1: {
        fontSize: '2rem',
        fontWeight: 400,
    },
    h2: {
        fontSize: '1.75rem',
        fontWeight: 400,
    },
    h3: {
        fontSize: '1.5rem',
        fontWeight: 400,
    },
    h4: {
        fontSize: '1.25rem',
        fontWeight: 400,
    },
    body1: {
        fontSize: '1rem',
    },
    body2: {
        fontSize: '0.875rem',
    },
}

const shape: ThemeOptions['shape'] = {
    borderRadius: 8,
}

const theme = createTheme({
    cssVariables: {
        colorSchemeSelector: 'data-mui-color-scheme',
        cssVarPrefix: 'medendo',
    },
    ...colorSchemes.light,
    typography,
    shape,
    // Here we override the default styles for the Drawer component
    components: {
        MuiCssBaseline: {
            styleOverrides: `
        /* Reserve the scrollbar gutter so page-height changes between views
           never shift the centered content horizontally */
        html {
            scrollbar-gutter: stable;
        }

        /* Markdown headers */
        h1 {
            font-size: 18px;
            font-weight: 600;
            margin: 6px 0;
        }
        h2 {
            font-size: 16px;
            font-weight: 600;
            margin: 6px 0;
        }
        h3 {
            font-size: 15px;
            font-weight: 600;
            margin: 4px 0;
        }
        h4 {
            font-size: 14px;
            font-weight: 600;
            margin: 4px 0;
        }
        h5 {
            font-size: 13px;
            font-weight: 600;
            margin: 3px 0;
        }
        h6 {
            font-size: 12px;
            font-weight: 600;
            margin: 3px 0;
        }

        /* Markdown paragraph defaults */
        p {
            margin: 4px 0;
            line-height: 1.4;
        }
    `,
        },

        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'medium',
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    padding: '32px',
                    gap: '24px',
                    borderRadius: '20px',
                },
            },
        },
        MuiStack: {
            defaultProps: {
                spacing: '24px',
            },
        },
        MuiFormControl: {
            defaultProps: {
                fullWidth: true,
            },
        },
        MuiSelect: {
            defaultProps: {
                variant: 'outlined',
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    padding: '12px',
                    borderRadius: '6px',
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                paper: {
                    padding: '12px',
                    borderRadius: '6px',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                // Sentence case and no tracking, like buttons: MUI's uppercase tab labels read as
                // shouting next to the sentence-case buttons around them
                root: {
                    textTransform: 'none',
                    letterSpacing: 0,
                    // MUI makes any tab with an icon and a label 72px tall, sized for an icon above
                    // the label. With the icon beside it instead, a tab keeps the plain tab's 48px.
                    variants: (['start', 'end'] as const).map((iconPosition) => ({
                        props: { iconPosition },
                        style: { '&.MuiTab-labelIcon': { minHeight: 48, paddingTop: 12, paddingBottom: 12 } },
                    })),
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: ({ theme, ownerState }) => ({
                    // MUI defaults to uppercase; sibling buttons on the same screen were
                    // inconsistent because only some opted out with their own sx override.
                    textTransform: 'none',
                    // MUI's default button tracking (~0.029em) is the "off the type scale"
                    // letter-spacing the audit flagged — buttons stay untracked everywhere.
                    letterSpacing: 0,
                    '&.Mui-focusVisible': {
                        outline: `2px solid ${theme.palette[resolveFocusRingColorKey(ownerState.color)].main}`,
                        outlineOffset: '2px',
                    },
                }),
                // Design system button scale: small 36/14px · medium 48/15px (default) · large 56/16px.
                // minWidth is a floor (88px) at medium/large only, so a short label like "OK" isn't
                // stretched but a row of same-size buttons still lines up — small stays unbound since
                // it's used for tight inline actions (table rows, chips) where that floor would crowd.
                sizeMedium: {
                    height: '48px',
                    minWidth: '88px',
                    padding: '8px 22px',
                    fontSize: '15px',
                },
                sizeSmall: {
                    height: '36px',
                    padding: '6px 16px',
                    fontSize: '14px',
                },
                sizeLarge: {
                    height: `${LARGE_BUTTON_HEIGHT}px`,
                    minWidth: '88px',
                    padding: '10px 26px',
                    fontSize: '16px',
                },
            },
            variants: [
                // The variants API's `style` callback only gets `theme`, not `ownerState` (unlike
                // styleOverrides above) — so the two colors heroAction actually ships in
                // (idle/primary, recording/error) are matched as separate prop combinations
                // instead of branching on ownerState.color inside one style function.
                {
                    props: { variant: 'heroAction' },
                    style: ({ theme }) => ({
                        borderRadius: '999px',
                        color: theme.palette.primary.contrastText,
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': {
                            backgroundColor: theme.palette.primary.dark,
                        },
                        // Only the built-in "contained" variant gets MUI's disabled background+text
                        // dimming for free — a custom variant like this one doesn't, so without this
                        // the button keeps its full-strength fill and just fades the label to a barely
                        // legible gray on top of it.
                        '&.Mui-disabled': {
                            backgroundColor: theme.palette.action.disabledBackground,
                            color: theme.palette.action.disabled,
                        },
                    }),
                },
                {
                    props: { variant: 'heroAction', color: 'error' },
                    style: ({ theme }) => ({
                        color: theme.palette.error.contrastText,
                        backgroundColor: theme.palette.error.main,
                        '&:hover': {
                            backgroundColor: theme.palette.error.dark,
                        },
                    }),
                },
            ],
        },
        MuiChip: {
            styleOverrides: {
                // Extra gap than the MUI defaults, kept proportional per size
                // (small label 8→10, medium 12→14; icons gain matching left padding)
                labelSmall: {
                    paddingLeft: '10px',
                    paddingRight: '10px',
                },
                labelMedium: {
                    paddingLeft: '14px',
                    paddingRight: '14px',
                },
                iconSmall: {
                    marginLeft: '7px',
                    marginRight: '-3px',
                },
                iconMedium: {
                    marginLeft: '8px',
                    marginRight: '-5px',
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    margin: 0,
                    // Slightly tighter than MuiPaper's own 20px, and scoped to dialogs only
                    // rather than raising the base token (which would also round every other
                    // Paper-based card/panel in the app).
                    borderRadius: '14px',
                    // MUI's Dialog hardcodes elevation=24, its heaviest stock shadow (three
                    // stacked layers, up to 46px blur) — replaced with a lighter one here since
                    // no border separates the dialog from the backdrop any more.
                    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08), 0px 8px 24px rgba(0, 0, 0, 0.12)',
                },
            },
        },
        MuiDialogTitle: {
            defaultProps: {
                sx: {
                    p: 0,
                    m: 0,
                    mt: '8px',
                    mb: '2px',
                    lineHeight: 1,
                    fontSize: '1.125rem',
                    fontWeight: 600,
                },
            },
        },
        MuiDialogContent: {
            defaultProps: {
                sx: {
                    p: 0,
                    m: 0,
                },
            },
        },
        MuiDialogActions: {
            defaultProps: {
                // Replaces MUI's default margin-left-between-siblings spacing (row-only) with a
                // gap, which keeps working once the buttons stack into a column on mobile.
                disableSpacing: true,
                sx: {
                    p: 0,
                    m: 0,
                    gap: 1,
                    // Narrow dialogs don't have room for two side-by-side buttons without
                    // wrapping their labels; column-reverse stacks them full width instead,
                    // with the confirming action on top per Material Design 3's guidance.
                    flexDirection: { xs: 'column-reverse', sm: 'row' },
                    '& > button': {
                        width: { xs: '100%', sm: 'auto' },
                    },
                },
            },
        },
    },
})

export default theme
